import {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { HttpHeader, ResponseHttpHeader } from '#/service/constants';
import {
  ApiError,
  AuthorizationError,
  AuthorizationErrorReason,
  GeoRestrictionError,
  UserAccessError,
  UserAccessErrorReason,
} from '#/service/error/errors';
import { getDeviceInfo } from '#/service/user/userSessionManager';
import { Dictionary, ignoreError } from '#/service/utils';
import { authActions } from '#/store/auth';
import { translationStore } from '#/store/translation/translation.store';

export enum AuthenticationChangeValues {
  Logout = 'logout',
}

export function setupCommonInterceptors(axios: AxiosInstance) {
  axios.interceptors.request.use(commonHeadersHandler);
  axios.interceptors.response.use(responseHandler, errorHandler);
}

export function commonHeadersHandler(
  config: AxiosRequestConfig
): AxiosRequestConfig {
  // TODO: Serialize and cache device info to not do it on every request
  if (config.headers) {
    config.headers[HttpHeader.DeviceInfo] = JSON.stringify(getDeviceInfo());
  }

  // One-core accepts locale on all calls made, easier than adding local to a variety of calls that require it
  const locale = translationStore.locale;

  config.params = { ...config.params, locale, ...urlWithParams() };

  // if the URL already includes a param, and we also put it in `config.params`, axios will include both
  // in the final URL. since we want to overwrite the original params with the ones returned from here,
  // we have to remove them from the original URL.
  if (config.url?.includes('?')) {
    const cleanedUrl = new URL(config.url, window.location.origin);
    Object.keys(config.params).forEach((name) =>
      cleanedUrl.searchParams.delete(name)
    );
    config.url = cleanedUrl.href;
  }

  return config;
}

export async function errorHandler(error: AxiosError): Promise<void> {
  const res = error.response;

  // There is no response we can validate, so let's ignore it.
  if (!res) {
    return;
  }

  // Log out user when needed
  if (
    res.headers[ResponseHttpHeader.AuthenticationChange] ===
    AuthenticationChangeValues.Logout
  ) {
    authActions.logout().catch(ignoreError);
  }

  if (isGeoRestrictionResponse(res)) {
    throw new GeoRestrictionError(res.statusText);
  }

  if (isUserAccessResponse(res)) {
    throw new UserAccessError(
      res.statusText,
      res.headers[UserAccessError.key] as UserAccessErrorReason
    );
  }

  if (isAuthorizationErrorResponse(res)) {
    throw new AuthorizationError(
      res.statusText,
      res.headers[AuthorizationError.reason] as AuthorizationErrorReason
    );
  }

  const errorDetails = {
    response: res,
    url: res.config.url,
    status: res.statusText,
    content: res.data ?? '',
    headers: JSON.stringify(res.headers),
  };

  throw new ApiError(res.statusText, errorDetails);
}

export async function responseHandler(
  res: AxiosResponse
): Promise<AxiosResponse> {
  // Log out user when needed
  if (
    res.headers[ResponseHttpHeader.AuthenticationChange] ===
    AuthenticationChangeValues.Logout
  ) {
    authActions.logout().catch(ignoreError);
  }

  return res;
}

// Gets query parameters from the browser's URL and prepares them for the API
const urlWithParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const paramsToForward = [
    'orgHash',
    'variationHash',
    'appId',
    'uiVersion',
    'env',
    'countryCode',
    'regionCode',
  ];

  return paramsToForward.reduce((acc: Dictionary<string, string>, p) => {
    if (urlParams.has(p)) {
      acc[p] = urlParams.get(p) || '';
    }

    return acc;
  }, {});
};

/** Determines whether the response indicates geo-restriction. */
const isGeoRestrictionResponse = (res: AxiosResponse) => {
  return (
    res.status === 403 &&
    (res.headers[ResponseHttpHeader.AccessRestricted] === 'true' ||
      res.headers[UserAccessError.key] === UserAccessErrorReason.LOCATION)
  );
};

/** Determines whether the response indicates user access denial. */
const isUserAccessResponse = (res: AxiosResponse) => {
  const reason = res.headers?.[UserAccessError.key];
  const isValidReason =
    reason &&
    Object.values(UserAccessErrorReason).includes(
      reason as UserAccessErrorReason
    );

  return res.status === 403 && isValidReason;
};

const isAuthorizationErrorResponse = (res: AxiosResponse) => {
  return (
    res.status === 401 && res.headers?.[ResponseHttpHeader.AuthorizationError]
  );
};
